/**
 * PlatenSizeRulesSection: which platen and platen variant each size prints
 * on, by product type (`org_platen_size_rules`, UI-SPEC S1).
 *
 * A line-for-line mirror of `PrintSizeRulesSection`, with the platen pair in
 * place of the cm inputs:
 *   - "Applies to" comes from `useOwnerClientTarget` (own org first, then the
 *     active junction clients). Switching resets the dirty buffer at once and
 *     re-targets the read, the write and the code-table pick-lists at the
 *     chosen org; the router's `assertOwnerOrSelf` is authoritative.
 *   - Product types and sizes are added from the SELECTED org's own code
 *     tables (`productSync.getCodeTablesForOrg`) through
 *     `PrintSizeCodeCombobox`, which degrades to manual entry while that
 *     query loads or fails. The section never waits on it.
 *   - Sizes a product type really uses (admins only): one read,
 *     `platenSize.getProductTypeSizes`, answers the TARGET org's product type
 *     -> size codes from its own synced products (supply rows excluded). Each
 *     type's size pick-list then lists only those sizes (a dual type such as
 *     2TEE joins its base type's list unless it has its own table, as the
 *     rules do), and "Add the sizes TEE uses" adds the missing ones. While
 *     that read loads, if it fails, or when a type has no synced products,
 *     the pick-list is the store's full size vocabulary and the add button
 *     is hidden: it degrades, it never blocks.
 *   - Each size row is the shared `PlatenAssignmentSelects` pair. A new row
 *     starts empty and blocks Save until both sides are chosen.
 *   - Bulk set (admins): each product type has a checkbox column (click,
 *     shift-click for a run, press and drag down the boxes to paint a run:
 *     `useDragSelect`) and one toolbar: ONE platen variant pick
 *     (`mode="combined"`, so a pick is always a complete pair) and "Apply to
 *     all N sizes" or "Apply to N selected sizes". Apply edits the buffer
 *     only, in one state update; Save is unchanged. The selection is per type
 *     and clears on Save, Discard, Reload and any "Applies to" switch (the
 *     list is keyed by the org); it stays after Apply so the result reads as
 *     the selection.
 *   - Local buffer + Save. Nothing changes on Shopify at save time, so there
 *     is no confirm dialog; but a save that ADDS or MOVES ruled sizes first
 *     asks the server what it affects (`platenSizeRules.previewImpact`) and
 *     says so through the warn-not-block dialog (DESIGN.md section 21) with
 *     `safeAnswer="proceed"`: "OK, I understand" is always green, "I'll fix
 *     it" always red (focus and Escape stay on it). The words carry the
 *     risk: one bold red line right above the buttons gives the count
 *     whenever products would stop with a print issue until their platen
 *     files are uploaded, open print issues are hit, the count was cut short
 *     ("At least N") or it failed; marked old products keep printing. The
 *     success toast links to Product updates when the save leaves the
 *     caller's own org with a rule (that list is always the caller's own,
 *     and it exists only while there is a rule). The org and version a save
 *     goes to are fixed when Save is clicked; while it counts and writes,
 *     "Applies to" and Discard are disabled, and an edit made while it
 *     counts drops that save rather than saving a buffer no longer on screen.
 *   - Optimistic concurrency: the save sends the `updatedAt` the buffer was
 *     loaded from (`expectedUpdatedAt`). If the rules changed since (another
 *     admin, or a size added from product sync), the server refuses with
 *     CONFLICT; the section says so and offers Reload, keeping the operator's
 *     edits on screen until they choose to reload. A background refetch never
 *     overwrites unsaved edits.
 *   - A failed read keeps "Applies to" on screen (so a failing client can be
 *     switched away from), and nothing says "no rules" until the org is known
 *     and its rules have answered.
 *
 * Platen info fields (own org, admins only): once the org has rules, any of
 * the four platen info metafields that is not mapped is offered through the
 * EXISTING `CreateDefinitionsBlock`, limited to `PLATEN_INFO_PURPOSES`.
 * Nothing is created on Shopify without that explicit click, and the block
 * reads the cached mappings list only (no Shopify discovery on this page).
 *
 * RIP routing overlap (own org only): when the org's RIP routing map sends
 * sizes to a different platen (resolved with the shared `resolvePlaten`), one
 * line per product type says which. It never blocks Save: platen variant
 * files print on the rule's platen.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { AlertTriangle, Frame, Info, Loader2, Plus, RefreshCw, Save, Trash2 } from 'lucide-react';
import {
  PLATEN_INFO_PURPOSES,
  PLATEN_SIZE_MAX_SIZES_PER_TYPE,
  RIP_CODE_REGEX,
  buildSizeRuns,
  groupSizesByPlatenVariant,
  resolvePlaten,
  ruleTypeCodesFor,
  type PlatenSizeRulesMap,
  type PlatenSizeRuleTable,
  type RipRoutingMap,
} from '@threadheads/shared';
import { trpc } from '@/lib/trpc';
import { useRole } from '@/hooks/use-role';
import { useVariantSelection } from '@/features/inventory/hooks/useVariantSelection';
import { useDragSelect } from '@/hooks/useDragSelect';
import { Checkbox } from '@/components/ui/checkbox';
import { formatPlatenSizeRange, platenVariantLabel } from '@/lib/platen-size-range';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { WarnNotBlockDialog } from '@/components/WarnNotBlockDialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  PlatenAssignmentSelects,
  type PlatenAssignmentDraft,
} from '@/components/shared/PlatenAssignmentSelects';
import {
  PrintSizeCodeCombobox,
  type PrintSizeCodeComboboxStatus,
  type PrintSizeCodeOption,
} from './PrintSizeCodeCombobox';
import { joinStoreNames, buildProductTypeCodeOptions, codeTablesStatus } from './org-code-labels';
import { useOwnerClientTarget } from '../hooks/useOwnerClientTarget';
import { CreateDefinitionsBlock } from '@/features/organization/components/product-data-lookup/CreateDefinitionsBlock';
import type { DiscoveryStatus } from '@/features/organization/components/product-data-lookup/MetafieldPicker';

// ---------------------------------------------------------------------------
// Types and pure helpers (module-level so identity is stable).
// ---------------------------------------------------------------------------

/** productType -> size -> a pair that may still be half chosen. */
type PlatenDraftMap = Record<string, Record<string, PlatenAssignmentDraft>>;

const EMPTY_PAIR: PlatenAssignmentDraft = { platen: null, platenVariant: null };
const PRODUCT_UPDATES_PATH = '/product-sync?tab=updates';
const SAVE_ERROR_TOAST_ID = 'platen-size-rules-save-error';

/** What `platenSizeRules.previewImpact` answers (contract 3). */
interface PlatenRulesImpact {
  newlyRuledSizes: number;
  productsAffected: number;
  openPrintIssuesAffected: number;
  /**
   * BLU-1238: products that keep printing their current files (an active skip
   * platen mark). Not in `productsAffected`: they need no file now.
   */
  productsKeptOnCurrentFiles?: number;
  /** The server stopped counting at its bound: the numbers are a floor. */
  truncated?: boolean;
  /**
   * The caller's own org only (never sent for a client): whether its standard
   * products could be set up with store SKUs, and whether that switch is on.
   * Standard products that are not set up are not in the counts, so the
   * dialog says so instead of implying nothing is affected.
   */
  standards?: { compositionOrg: boolean; declaredMode: 'off' | 'on' };
}

/**
 * What the save warning says, from the one impact read (null: the read
 * failed). `redLines` are the harms, shown in bold red right above the
 * buttons; empty when nothing could stop.
 */
interface ImpactCopy {
  titleKey: string;
  sizes: number | null;
  kept: number;
  redLines: Array<{ key: string; count?: number }>;
}

function impactCopy(impact: PlatenRulesImpact | null): ImpactCopy {
  if (!impact) {
    return { titleKey: 'platenSize.impact.titleUncounted', sizes: null, kept: 0, redLines: [{ key: 'platenSize.impact.failed' }] };
  }
  const cutShort = impact.truncated === true;
  const stops = impact.productsAffected;
  const issues = impact.openPrintIssuesAffected;
  const kept = impact.productsKeptOnCurrentFiles ?? 0;
  const redLines: ImpactCopy['redLines'] = [];
  if (stops > 0) {
    redLines.push({ key: cutShort ? 'platenSize.impact.stopsAtLeast' : 'platenSize.impact.stops', count: stops });
  }
  if (issues > 0) {
    redLines.push({ key: cutShort ? 'platenSize.impact.issuesAtLeast' : 'platenSize.impact.issuesStop', count: issues });
  }
  // Cut short with nothing counted yet: say so, in red, and in the title.
  if (cutShort && redLines.length === 0) {
    return { titleKey: 'platenSize.impact.titleUncounted', sizes: impact.newlyRuledSizes, kept, redLines: [{ key: 'platenSize.impact.cutShort' }] };
  }
  const titleKey =
    redLines.length > 0
      ? 'platenSize.impact.titleStops'
      : kept > 0
        ? 'platenSize.impact.titleKept'
        : standardsNoteKey(impact)
          ? 'platenSize.impact.titleNoneCounted'
          : 'platenSize.impact.titleNone';
  return { titleKey, sizes: impact.newlyRuledSizes, kept, redLines };
}

/** The standards note under the counts, or null (a non-composition org, or no `standards`). */
function standardsNoteKey(impact: PlatenRulesImpact | null | undefined): string | null {
  if (!impact?.standards?.compositionOrg) return null;
  return impact.standards.declaredMode === 'on' ? 'platenSize.impact.standardsOn' : 'platenSize.impact.standardsOff';
}

/**
 * Where a save goes and the version it is built on, fixed when Save is
 * clicked. A save that waits on the impact check and its dialog still writes
 * here, whatever "Applies to" says by the time the operator answers.
 */
interface SaveTarget {
  /** `organizationId` for the write: undefined for the own org. */
  organizationId: string | undefined;
  /** The `updatedAt` the buffer was loaded from (contract 2). */
  expectedUpdatedAt: string | null;
}

/** A save waiting on the impact dialog: the counts, or null when they could not be read. */
interface PendingImpactSave {
  impact: PlatenRulesImpact | null;
  map: PlatenSizeRulesMap;
  target: SaveTarget;
}

/**
 * `getRules().updatedAt` (contract 2), or null. Read structurally so the
 * section works against a server that does not send it yet (every save then
 * sends null, which that server ignores).
 */
function readUpdatedAt(data: unknown): string | null {
  if (typeof data !== 'object' || data === null || !('updatedAt' in data)) return null;
  const value = (data as { updatedAt?: unknown }).updatedAt;
  return typeof value === 'string' ? value : null;
}

/** True when `next` rules a size `prev` did not, or moves one to another platen variant. */
function addsOrChangesRuledSizes(prev: PlatenSizeRulesMap, next: PlatenSizeRulesMap): boolean {
  for (const [pt, sizes] of Object.entries(next)) {
    for (const [code, leaf] of Object.entries(sizes)) {
      const before = prev[pt]?.[code];
      if (!before || before.platen !== leaf.platen || before.platenVariant !== leaf.platenVariant) return true;
    }
  }
  return false;
}

const isConflict = (error: unknown): boolean =>
  (error as { data?: { code?: string } } | null)?.data?.code === 'CONFLICT';
/** BLU-1238: the server's save guard (a store SKU org whose products are not kept yet). */
const isNotReady = (error: unknown): boolean =>
  (error as { data?: { code?: string } } | null)?.data?.code === 'PRECONDITION_FAILED';

const isCodeValid = (v: string): boolean => RIP_CODE_REGEX.test(v);
const isComplete = (pair: PlatenAssignmentDraft): boolean =>
  pair.platen !== null && pair.platenVariant !== null;

function toDraftMap(map: PlatenSizeRulesMap | undefined): PlatenDraftMap {
  const out: PlatenDraftMap = {};
  for (const [pt, sizes] of Object.entries(map ?? {})) {
    out[pt] = {};
    for (const [code, leaf] of Object.entries(sizes)) {
      out[pt][code] = { platen: leaf.platen, platenVariant: leaf.platenVariant };
    }
  }
  return out;
}

/** Complete leaves only, one product type's worth. */
function completeTable(sizes: Record<string, PlatenAssignmentDraft>): PlatenSizeRuleTable {
  const table: PlatenSizeRuleTable = {};
  for (const [code, pair] of Object.entries(sizes)) {
    if (pair.platen !== null && pair.platenVariant !== null) {
      table[code] = { platen: pair.platen, platenVariant: pair.platenVariant };
    }
  }
  return table;
}

/** Drop incomplete leaves and empty product types before Save. Save is gated
 *  on `anyInvalid` too, so this is belt-and-braces. */
function cleanMapForSave(map: PlatenDraftMap): PlatenSizeRulesMap {
  const out: PlatenSizeRulesMap = {};
  for (const [pt, sizes] of Object.entries(map)) {
    const table = completeTable(sizes);
    if (Object.keys(table).length > 0) out[pt] = table;
  }
  return out;
}

/** Whether a rules map holds at least one size rule. */
const hasAnyRule = (map: PlatenSizeRulesMap): boolean =>
  Object.values(map).some((table) => Object.keys(table).length > 0);

/** The shared size order (the same ladder the summary chips use), never
 *  alphabetical. Unknown codes keep their order at the end. */
const orderSizeCodes = (codes: readonly string[]): string[] => buildSizeRuns(codes).flat();

// ---------------------------------------------------------------------------
// Section.
// ---------------------------------------------------------------------------

export function PlatenSizeRulesSection(): JSX.Element {
  const { t } = useTranslation('settings');
  const navigate = useNavigate();
  const { isAdmin } = useRole();
  const admin = isAdmin();

  const { ownOrgId, clientOptions, selectedOrgId, setSelectedOrgId, isOwnOrgSelected, targetOrgInput } =
    useOwnerClientTarget();

  const rulesQuery = trpc.platenSizeRules.getRules.useQuery(
    { organizationId: targetOrgInput },
    { enabled: !!selectedOrgId },
  );
  // The SELECTED org's own product-sync code tables. The section never gates
  // on this query: the combobox degrades to manual entry while it loads or
  // fails.
  const codeTablesQuery = trpc.productSync.getCodeTablesForOrg.useQuery(
    { organizationId: targetOrgInput },
    { enabled: true },
  );
  // The caller's OWN RIP routing map, for the overlap line. Only asked for
  // while the own org is selected; a failure just hides the line.
  const ripMapQuery = trpc.ripRouting.getRipRoutingMap.useQuery(undefined, {
    enabled: isOwnOrgSelected && !!selectedOrgId,
    staleTime: 60_000,
  });
  const ripMap: RipRoutingMap | null = isOwnOrgSelected ? (ripMapQuery.data?.map ?? null) : null;
  // The caller's own metafield mappings (the same cached list the
  // definitions block reads), to know which platen info fields are missing.
  const mappingsQuery = trpc.shopifyMetafieldMappings.list.useQuery(undefined, {
    enabled: admin && isOwnOrgSelected && !!selectedOrgId,
    staleTime: 60_000,
  });
  // The sizes each product type of the SELECTED org uses, for the per-type
  // pick-lists and "Add the sizes TEE uses". Admins only (the only ones with
  // add controls). A hint, never a gate: sizes do not change on a rules save,
  // so it is cached for 5 minutes and not refetched after one.
  const typeSizesQuery = trpc.platenSizeRules.getProductTypeSizes.useQuery(
    { organizationId: targetOrgInput },
    { enabled: admin && !!selectedOrgId, staleTime: 5 * 60_000 },
  );
  const utils = trpc.useUtils();

  const [localMap, setLocalMap] = useState<PlatenDraftMap>({});
  // The buffer on screen right now, for an impact check that resolves after
  // an edit, a Discard or a client switch (each replaces the buffer object).
  const localMapRef = useRef(localMap);
  localMapRef.current = localMap;
  const [hasChanges, setHasChangesState] = useState(false);
  // Mirrors `hasChanges` synchronously, so the hydrate effect never reads a
  // value from before a same-render reset.
  const hasChangesRef = useRef(false);
  const setHasChanges = useCallback((value: boolean) => {
    hasChangesRef.current = value;
    setHasChangesState(value);
  }, []);
  const [openItems, setOpenItems] = useState<string[]>([]);
  // Bumped on Save success, Discard and Reload: each product type clears its
  // bulk selection when it changes.
  const [selectionEpoch, setSelectionEpoch] = useState(0);
  // The version the buffer was loaded from, sent back on save (contract 2).
  const [baseUpdatedAt, setBaseUpdatedAt] = useState<string | null>(null);
  // The server refused a stale save: say so and offer Reload.
  const [conflict, setConflict] = useState(false);
  // BLU-1238: the save guard's refusal, shown until a save lands or the org changes.
  const [notReady, setNotReady] = useState(false);
  const [checkingImpact, setCheckingImpact] = useState(false);
  const [pendingImpact, setPendingImpact] = useState<PendingImpactSave | null>(null);
  // The last dialog shown, so it keeps its text while it animates out.
  const [shownImpact, setShownImpact] = useState<PendingImpactSave | null>(null);

  // Reset the dirty buffer IMMEDIATELY on a client switch, before the new
  // org's query resolves, so a Save in that gap can never write the previous
  // org's buffer to the newly selected org. A save still waiting on its impact
  // dialog belonged to the previous org too, so it goes with the buffer.
  useEffect(() => {
    setLocalMap({});
    setHasChanges(false);
    setConflict(false);
    setNotReady(false);
    setBaseUpdatedAt(null);
    setPendingImpact(null);
    setCheckingImpact(false);
  }, [selectedOrgId, setHasChanges]);

  // Hydrate from the server (initial load, client switch, after a save), but
  // NEVER over unsaved edits: a background refetch (window focus) must not
  // wipe what the operator is typing. A save built on an older version is
  // then refused by the server (CONFLICT) instead of silently winning.
  useEffect(() => {
    if (!rulesQuery.data || hasChangesRef.current) return;
    setLocalMap(toDraftMap(rulesQuery.data.map));
    setBaseUpdatedAt(readUpdatedAt(rulesQuery.data));
  }, [rulesQuery.data]);

  // Leaving the page with unsaved edits asks first.
  useEffect(() => {
    if (!admin || !hasChanges) return undefined;
    const onBeforeUnload = (event: BeforeUnloadEvent): void => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [admin, hasChanges]);

  const updateMutation = trpc.platenSizeRules.updateRules.useMutation({
    onSuccess: (result, variables) => {
      toast.dismiss(SAVE_ERROR_TOAST_ID);
      // Product updates exists only while the org has a rule, and it always
      // lists the CALLER's own products (`productUpdates.getPage` takes no
      // org). So the toast points there only when this save left the
      // caller's own org with at least one rule.
      if (variables.organizationId === undefined && hasAnyRule(variables.map)) {
        toast.success(t('platenSize.saved'), {
          action: {
            label: t('platenSize.openProductUpdates'),
            onClick: () => navigate(PRODUCT_UPDATES_PATH),
          },
        });
      } else {
        toast.success(t('platenSize.savedPlain'));
      }
      // The new version at once, so an edit made before the refetch lands
      // still saves against the right one.
      const nextVersion = readUpdatedAt(result);
      if (nextVersion) setBaseUpdatedAt(nextVersion);
      setConflict(false);
      setNotReady(false);
      setHasChanges(false);
      setSelectionEpoch((n) => n + 1);
      void utils.platenSizeRules.getRules.invalidate();
      // Product sync shows its Product updates tab and its platen groups from
      // the org's platen context read, and the worklist pages are cached for
      // 5 minutes: drop both, so "Open Product updates" lands on the tab and
      // lists what these rules need, even if product sync was opened earlier
      // in this session.
      void utils.productSync.getPlatenSizeContext.invalidate();
      void utils.productUpdates.getPage.invalidate();
    },
    // The buffer is kept: nothing is reset on failure.
    onError: (e) => {
      if (isConflict(e)) {
        // Someone else saved (or a size was added from product sync) since
        // this buffer loaded. Persistent inline notice with Reload, below.
        toast.dismiss(SAVE_ERROR_TOAST_ID);
        setConflict(true);
        return;
      }
      if (isNotReady(e)) {
        // Not a fault to retry: the plain refusal, inline, edits kept.
        toast.dismiss(SAVE_ERROR_TOAST_ID);
        setNotReady(true);
        return;
      }
      toast.error(t('platenSize.saveError', { message: e.message }), {
        id: SAVE_ERROR_TOAST_ID,
        duration: Infinity,
      });
    },
  });

  const serverMap: PlatenSizeRulesMap = rulesQuery.data?.map ?? {};
  const hasSavedRules = hasAnyRule(serverMap);

  const unmappedInfoFieldCount = useMemo(() => {
    if (!mappingsQuery.data) return 0;
    const mapped = new Set<string>(mappingsQuery.data.map((m) => m.purpose));
    return PLATEN_INFO_PURPOSES.filter((purpose) => !mapped.has(purpose)).length;
  }, [mappingsQuery.data]);
  // A failed mappings read still shows the block, which then says why it is
  // paused (its own errored state), instead of hiding the whole offer.
  const showInfoFields =
    admin && isOwnOrgSelected && hasSavedRules && (mappingsQuery.isError || unmappedInfoFieldCount > 0);
  const infoDiscoveryStatus: DiscoveryStatus = mappingsQuery.isLoading
    ? 'loading'
    : mappingsQuery.isError
      ? 'errored'
      : 'loaded';

  const productTypeCodes = useMemo(
    () => Object.keys(localMap).sort((a, b) => a.localeCompare(b)),
    [localMap],
  );

  // The complete rules as they would save, so a dual type (2TEE) joins its
  // base type's size list exactly when it would print on that table.
  const savedShape = useMemo(() => cleanMapForSave(localMap), [localMap]);
  // Per product type: the sizes its products use (null until the read
  // answers, or when it failed), in the shared size order.
  const usedSizesByType = useMemo(() => {
    const out = new Map<string, string[] | null>();
    const sizesByType = typeSizesQuery.data?.sizesByType;
    for (const pt of productTypeCodes) {
      if (!sizesByType) {
        out.set(pt, null);
        continue;
      }
      const union = new Set<string>();
      for (const code of ruleTypeCodesFor(savedShape, pt)) {
        if (!Object.prototype.hasOwnProperty.call(sizesByType, code)) continue;
        for (const size of sizesByType[code] ?? []) union.add(size);
      }
      out.set(pt, orderSizeCodes([...union]).slice(0, PLATEN_SIZE_MAX_SIZES_PER_TYPE));
    }
    return out;
  }, [typeSizesQuery.data, productTypeCodes, savedShape]);

  const anyInvalid = useMemo(
    () => Object.values(localMap).some((sizes) => Object.values(sizes).some((pair) => !isComplete(pair))),
    [localMap],
  );

  // ---- local-edit mutators ----

  const setPair = (pt: string, code: string, pair: PlatenAssignmentDraft): void => {
    setLocalMap((prev) => ({ ...prev, [pt]: { ...(prev[pt] ?? {}), [code]: pair } }));
    setHasChanges(true);
  };

  const removeSize = (pt: string, code: string): void => {
    setLocalMap((prev) => {
      const sizes = prev[pt];
      if (!sizes) return prev;
      const next = { ...sizes };
      delete next[code];
      return { ...prev, [pt]: next };
    });
    setHasChanges(true);
  };

  const removeProductType = (pt: string): void => {
    setLocalMap((prev) => {
      const next = { ...prev };
      delete next[pt];
      return next;
    });
    setHasChanges(true);
  };

  // Bulk set: every listed size of one type gets the full pair, in ONE state
  // update (one render, whatever the count). Sizes no longer in the type are
  // skipped.
  const applyPair = (pt: string, codes: readonly string[], pair: PlatenAssignmentDraft): void => {
    if (codes.length === 0 || !isComplete(pair)) return;
    setLocalMap((prev) => {
      const current = prev[pt];
      if (!current) return prev;
      const sizes = { ...current };
      for (const code of codes) {
        if (Object.prototype.hasOwnProperty.call(sizes, code)) {
          sizes[code] = { platen: pair.platen, platenVariant: pair.platenVariant };
        }
      }
      return { ...prev, [pt]: sizes };
    });
    setHasChanges(true);
  };

  const addSizes = (pt: string, codes: string[]): void => {
    if (codes.length === 0) return;
    setLocalMap((prev) => {
      const sizes = { ...(prev[pt] ?? {}) };
      for (const code of codes) if (!Object.prototype.hasOwnProperty.call(sizes, code)) sizes[code] = EMPTY_PAIR;
      return { ...prev, [pt]: sizes };
    });
    setHasChanges(true);
  };

  const handleSelectSizeCode = (pt: string, rawCode: string): boolean => {
    const code = rawCode.trim().toUpperCase();
    if (!isCodeValid(code)) {
      toast.error(t('platenSize.invalidCode'));
      return false;
    }
    const existing = localMap[pt];
    if (existing && Object.prototype.hasOwnProperty.call(existing, code)) {
      toast.error(t('platenSize.duplicateSize', { size: code, productType: pt }));
      return false;
    }
    addSizes(pt, [code]);
    return true;
  };

  const handleSelectProductType = (rawCode: string): boolean => {
    const code = rawCode.trim().toUpperCase();
    if (!isCodeValid(code)) {
      toast.error(t('platenSize.invalidCode'));
      return false;
    }
    if (productTypeCodes.includes(code)) {
      toast.error(t('platenSize.duplicateType', { code }));
      return false;
    }
    setLocalMap((prev) => ({ ...prev, [code]: {} }));
    setOpenItems((prev) => (prev.includes(code) ? prev : [...prev, code]));
    setHasChanges(true);
    return true;
  };

  const saveMap = (map: PlatenSizeRulesMap, target: SaveTarget): void => {
    // A variable, not a literal: `expectedUpdatedAt` is part of the save
    // contract (sent even as null, meaning "there were no rules").
    const input = { organizationId: target.organizationId, map, expectedUpdatedAt: target.expectedUpdatedAt };
    updateMutation.mutate(input);
  };

  const handleSave = async (): Promise<void> => {
    const map = cleanMapForSave(localMap);
    // Fixed now, before anything awaits: the org and version this buffer
    // belongs to. "Applies to" and Discard are disabled while the check runs,
    // and these are what the dialog's "OK, I understand" saves with.
    const target: SaveTarget = { organizationId: targetOrgInput, expectedUpdatedAt: baseUpdatedAt };
    // Only a save that adds or moves ruled sizes can create work; removals
    // and no-op saves go straight through.
    if (!addsOrChangesRuledSizes(serverMap, map)) {
      saveMap(map, target);
      return;
    }
    const checkedBuffer = localMap;
    setCheckingImpact(true);
    let impact: PlatenRulesImpact | null = null;
    try {
      impact = await utils.platenSizeRules.previewImpact.fetch(
        { targetOrgId: target.organizationId, rules: map },
        { staleTime: 0 },
      );
    } catch {
      // Could not count: still warn (with the general sentence), never block.
      impact = null;
    } finally {
      setCheckingImpact(false);
    }
    // The buffer moved on while the count ran (an edit, or a client switch
    // that reset it): the count describes edits no longer on screen, so
    // nothing saves. Save is enabled again and checks what is there now.
    if (localMapRef.current !== checkedBuffer) return;
    if (impact && impact.newlyRuledSizes === 0) {
      saveMap(map, target);
      return;
    }
    const pending: PendingImpactSave = { impact, map, target };
    setShownImpact(pending);
    setPendingImpact(pending);
  };

  const handleDiscard = (): void => {
    setLocalMap(toDraftMap(rulesQuery.data?.map));
    setBaseUpdatedAt(readUpdatedAt(rulesQuery.data));
    setConflict(false);
    setHasChanges(false);
    setSelectionEpoch((n) => n + 1);
  };

  // Reload after a refused save: the latest rules replace the buffer.
  const handleReload = async (): Promise<void> => {
    const result = await rulesQuery.refetch();
    if (!result.data) return;
    setLocalMap(toDraftMap(result.data.map));
    setBaseUpdatedAt(readUpdatedAt(result.data));
    setConflict(false);
    setHasChanges(false);
    setSelectionEpoch((n) => n + 1);
  };

  // ---- the selected org's code-table pick-lists ----

  const { typeOptions, typeStoreNamesByCode } = useMemo(
    () =>
      buildProductTypeCodeOptions(codeTablesQuery.data?.productTypes, productTypeCodes, {
        custom: t('production.printSizeRules.badgeCustom'),
        singleSize: t('production.printSizeRules.badgeSingleSize'),
        alreadyAdded: t('production.printSizeRules.alreadyAdded'),
        invalidCode: t('production.codeOptions.invalidCode'),
      }),
    [codeTablesQuery.data, productTypeCodes, t],
  );
  const typeComboboxStatus = codeTablesStatus(codeTablesQuery, typeOptions.length);

  // Org-wide size vocabulary in the shared size order; per-type `disabled`
  // is layered on inside PlatenSizeProductTypeItem.
  const sizeOptionsBase: PrintSizeCodeOption[] = useMemo(() => {
    const rows = codeTablesQuery.data?.sizes ?? [];
    const labelByCode = new Map(rows.map((row) => [row.code, joinStoreNames(row.storeNames)]));
    return orderSizeCodes(rows.map((row) => row.code)).map((code) => ({
      code,
      label: labelByCode.get(code) ?? '',
    }));
  }, [codeTablesQuery.data]);

  const sizeStoreNamesByCode = useMemo(() => {
    const map = new Map<string, string[]>();
    for (const row of codeTablesQuery.data?.sizes ?? []) map.set(row.code, row.storeNames);
    return map;
  }, [codeTablesQuery.data]);

  const sizeComboboxStatus = codeTablesStatus(codeTablesQuery, sizeOptionsBase.length);

  // ---- states ----
  // Never "no rules" before the org is known and its rules have answered
  // (a disabled query is pending, not loaded).
  const rulesPending = !selectedOrgId || rulesQuery.isPending;
  const rulesReady = !rulesQuery.isError && !rulesPending;
  const showEmptyState = rulesReady && productTypeCodes.length === 0;
  const impactView = pendingImpact ?? shownImpact;
  const standardsNote = standardsNoteKey(impactView?.impact);
  const copy = impactCopy(impactView?.impact ?? null);
  // A save is on its way (counting its impact, or writing): the org it goes
  // to and the buffer it was built from must not change under it.
  const saveInFlight = checkingImpact || updateMutation.isPending;

  return (
    <div className="space-y-4">
      {/* 1. Framing. */}
      <p className="flex items-start gap-2 text-sm text-muted-foreground">
        <Info className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
        <span className="max-w-prose leading-relaxed">{t('platenSize.framing')}</span>
      </p>

      {/* 2. Applies to. */}
      <div className="grid max-w-xs gap-1.5">
        <label htmlFor="platen-size-rules-client" className="text-sm font-medium">
          {t('platenSize.appliesTo')}
        </label>
        <Select value={selectedOrgId ?? ownOrgId} onValueChange={setSelectedOrgId} disabled={!ownOrgId || saveInFlight}>
          <SelectTrigger id="platen-size-rules-client" className="h-11">
            <SelectValue placeholder={t('platenSize.selectOrganization')} />
          </SelectTrigger>
          <SelectContent>
            {ownOrgId && <SelectItem value={ownOrgId}>{t('platenSize.thisOrganization')}</SelectItem>}
            {clientOptions.map((c) => (
              <SelectItem key={c.id} value={c.id}>
                {c.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* 3. Body. A failed read shows here, under "Applies to", so a client
          whose read fails can still be switched away from. */}
      {rulesQuery.isError ? (
        <div className="space-y-3">
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" aria-hidden="true" />
            <AlertDescription>{t('platenSize.loadError', { message: rulesQuery.error?.message ?? '' })}</AlertDescription>
          </Alert>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => void rulesQuery.refetch()}
            disabled={rulesQuery.isFetching}
            className="gap-1.5"
          >
            {rulesQuery.isFetching && (
              <Loader2 className="h-3.5 w-3.5 animate-spin motion-reduce:animate-none" aria-hidden="true" />
            )}
            {t('platenSize.retry')}
          </Button>
        </div>
      ) : rulesPending ? (
        <div className="space-y-2" aria-busy="true">
          <Skeleton className="h-12 w-full rounded-lg" />
          <Skeleton className="h-12 w-full rounded-lg" />
        </div>
      ) : showEmptyState ? (
        <div className="flex flex-col items-center gap-3 rounded-lg border border-dashed py-10 text-center">
          <div className="rounded-full bg-muted p-3">
            <Frame className="h-6 w-6 text-muted-foreground" aria-hidden="true" />
          </div>
          <div className="space-y-1">
            <p className="text-sm font-medium">{t('platenSize.emptyTitle')}</p>
            {/* "Add a product type below" points at a control only admins get. */}
            <p className="mx-auto max-w-sm text-sm text-muted-foreground">
              {admin ? t('platenSize.emptyBody') : t('platenSize.emptyBodyNonAdmin')}
            </p>
          </div>
        </div>
      ) : (
        // Keyed by the org: an "Applies to" switch remounts every type, so no
        // bulk selection or toolbar pick carries from one org to another.
        <Accordion
          key={selectedOrgId ?? 'none'}
          type="multiple"
          value={openItems}
          onValueChange={setOpenItems}
          className="space-y-2"
        >
          {productTypeCodes.map((pt) => (
            <PlatenSizeProductTypeItem
              key={pt}
              productType={pt}
              sizes={localMap[pt] ?? {}}
              serverSizes={serverMap[pt]}
              typeStoreNames={typeStoreNamesByCode.get(pt)}
              admin={admin}
              ripMap={ripMap}
              onSetPair={setPair}
              onRemoveSize={removeSize}
              onSelectSize={handleSelectSizeCode}
              onAddSizes={addSizes}
              onApplyPair={applyPair}
              selectionEpoch={selectionEpoch}
              onRemoveProductType={removeProductType}
              sizeOptions={sizeOptionsBase}
              sizeStatus={sizeComboboxStatus}
              sizeStoreNamesByCode={sizeStoreNamesByCode}
              usedSizeCodes={usedSizesByType.get(pt) ?? null}
            />
          ))}
        </Accordion>
      )}

      {/* 4. Add product type (admin only), from the selected org's own codes. */}
      {admin && rulesReady && (
        <PrintSizeCodeCombobox
          emptyLabel={t('production.codeOptions.noMatches')}
          options={typeOptions}
          status={typeComboboxStatus}
          onSelect={handleSelectProductType}
          placeholder={t('platenSize.typeComboboxPlaceholder')}
          ariaLabel={t('platenSize.typeComboboxAria')}
          addButtonLabel={t('platenSize.addProductType')}
          createLabel={(code) => t('platenSize.createCode', { code })}
          errorHint={t('platenSize.codeTablesError')}
          className="sm:w-56"
        />
      )}

      {/* 5a0. BLU-1238: the server's save guard refused (existing products
          are not kept on their current files yet). Plain words, no action. */}
      {admin && notReady && (
        <Alert className="border-amber-300 bg-amber-50 text-amber-900" data-testid="platen-size-not-ready">
          <AlertTriangle className="h-4 w-4 text-amber-700" aria-hidden="true" />
          <AlertDescription>{t('platenSize.notReady')}</AlertDescription>
        </Alert>
      )}

      {/* 5a. A refused stale save: persistent, with Reload. The edits stay on
          screen until the operator reloads. */}
      {admin && conflict && (
        <Alert className="border-amber-300 bg-amber-50 text-amber-900" data-testid="platen-size-conflict">
          <AlertTriangle className="h-4 w-4 text-amber-700" aria-hidden="true" />
          <AlertDescription className="flex flex-wrap items-center justify-between gap-3">
            <span className="space-y-0.5">
              <span className="block font-medium">{t('platenSize.conflict')}</span>
              <span className="block text-xs">{t('platenSize.conflictKeep')}</span>
            </span>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => void handleReload()}
              disabled={rulesQuery.isFetching}
              className="gap-1.5 border-amber-300 bg-background motion-reduce:transition-none"
            >
              {rulesQuery.isFetching ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin motion-reduce:animate-none" aria-hidden="true" />
              ) : (
                <RefreshCw className="h-3.5 w-3.5" aria-hidden="true" />
              )}
              {t('platenSize.reload')}
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {/* 5. Save row (admin + dirty only). */}
      {admin && hasChanges && (
        <div className="flex flex-wrap items-center justify-end gap-3">
          {anyInvalid && (
            <span className="text-xs text-amber-700" role="status">
              {t('platenSize.saveBlocked')}
            </span>
          )}
          <Button
            type="button"
            variant="ghost"
            onClick={handleDiscard}
            disabled={saveInFlight}
            className="motion-reduce:transition-none"
          >
            {t('platenSize.discard')}
          </Button>
          <Button
            type="button"
            onClick={() => void handleSave()}
            // Also disabled while the rules query is (re)fetching, so Save can
            // never fire against a buffer not confirmed to belong to the
            // selected org, and while a refused save waits for Reload.
            disabled={
              updateMutation.isPending || checkingImpact || anyInvalid || rulesQuery.isFetching || conflict
            }
            className="gap-1.5 bg-threadheads-purple text-white hover:bg-threadheads-purple/90 motion-reduce:transition-none"
          >
            {updateMutation.isPending || checkingImpact ? (
              <Loader2 className="h-4 w-4 animate-spin motion-reduce:animate-none" aria-hidden="true" />
            ) : (
              <Save className="h-4 w-4" aria-hidden="true" />
            )}
            {t('platenSize.save')}
            {checkingImpact && <span className="sr-only">{t('platenSize.checkingImpact')}</span>}
          </Button>
        </div>
      )}

      {/* 6. Platen info fields on the caller's own store (explicit click
          only). The count is the block's own note, under its one heading. */}
      {showInfoFields && (
        <CreateDefinitionsBlock
          admin={admin}
          purposes={PLATEN_INFO_PURPOSES}
          buttonLabel={t('platenSize.infoFields.button')}
          discoveryStatus={infoDiscoveryStatus}
          onRetryDiscovery={() => void mappingsQuery.refetch()}
          note={
            mappingsQuery.data
              ? t('platenSize.infoFields.missing', {
                  count: unmappedInfoFieldCount,
                  total: PLATEN_INFO_PURPOSES.length,
                })
              : undefined
          }
        />
      )}

      {/* 7. Non-admin. The empty state already says so in its own words. */}
      {!admin && !showEmptyState && <p className="text-sm text-muted-foreground">{t('platenSize.nonAdmin')}</p>}

      {/* The impact of a save that adds or moves ruled sizes (warn, never
          block). Going ahead is the normal answer here (Brett, 2026-10-28):
          "OK, I understand" is always green and saves, "I'll fix it" always
          red and keeps editing. The bold red line above the buttons carries
          any harm, with its count. */}
      <WarnNotBlockDialog
        open={pendingImpact !== null}
        contentClassName="max-w-md"
        safeAnswer="proceed"
        title={t(copy.titleKey)}
        description={
          <>
            {(copy.sizes !== null || copy.kept > 0) && (
              <ul className="space-y-0.5 tabular-nums">
                {copy.sizes !== null && <li>{t('platenSize.impact.sizes', { count: copy.sizes })}</li>}
                {copy.kept > 0 && <li>{t('platenSize.impact.kept', { count: copy.kept })}</li>}
              </ul>
            )}
            {standardsNote && <p data-testid="platen-size-impact-standards">{t(standardsNote)}</p>}
          </>
        }
        onProceed={() => {
          const current = pendingImpact;
          setPendingImpact(null);
          // The org and version captured when Save was clicked, never the
          // ones on screen now.
          if (current) saveMap(current.map, current.target);
        }}
        onFix={() => setPendingImpact(null)}
      >
        {copy.redLines.length > 0 && (
          <div
            className="space-y-1 text-sm font-medium tabular-nums text-destructive"
            data-testid="platen-size-impact-red-line"
          >
            {copy.redLines.map((line) => (
              <p key={line.key}>{line.count === undefined ? t(line.key) : t(line.key, { count: line.count })}</p>
            ))}
          </div>
        )}
      </WarnNotBlockDialog>
    </div>
  );
}

// ---------------------------------------------------------------------------
// One product type: size code -> platen + platen variant rows.
// ---------------------------------------------------------------------------

interface PlatenSizeProductTypeItemProps {
  productType: string;
  sizes: Record<string, PlatenAssignmentDraft>;
  /** The saved leaves, for the per-Select dirty border. */
  serverSizes: PlatenSizeRuleTable | undefined;
  typeStoreNames: string[] | undefined;
  admin: boolean;
  /** The caller's own RIP routing map; null for a client or when unknown. */
  ripMap: RipRoutingMap | null;
  onSetPair: (pt: string, code: string, pair: PlatenAssignmentDraft) => void;
  onRemoveSize: (pt: string, code: string) => void;
  onSelectSize: (pt: string, code: string) => boolean;
  onAddSizes: (pt: string, codes: string[]) => void;
  /** Bulk set: the full pair onto the listed sizes of this type. */
  onApplyPair: (pt: string, codes: readonly string[], pair: PlatenAssignmentDraft) => void;
  /** Changes on Save success, Discard and Reload: the selection clears. */
  selectionEpoch: number;
  onRemoveProductType: (pt: string) => void;
  sizeOptions: PrintSizeCodeOption[];
  sizeStatus: PrintSizeCodeComboboxStatus;
  sizeStoreNamesByCode: Map<string, string[]>;
  /**
   * The sizes this type's products use, in size order: null while the read
   * loads or after it failed (the full store list is offered), empty when it
   * answered with none (full list, plus a note), else the type's own list.
   */
  usedSizeCodes: string[] | null;
}

function PlatenSizeProductTypeItem({
  productType: pt,
  sizes,
  serverSizes,
  typeStoreNames,
  admin,
  ripMap,
  onSetPair,
  onRemoveSize,
  onSelectSize,
  onAddSizes,
  onApplyPair,
  selectionEpoch,
  onRemoveProductType,
  sizeOptions,
  sizeStatus,
  sizeStoreNamesByCode,
  usedSizeCodes,
}: PlatenSizeProductTypeItemProps): JSX.Element {
  const { t } = useTranslation('settings');
  const codes = useMemo(() => orderSizeCodes(Object.keys(sizes)), [sizes]);

  // Bulk selection over this type's sizes. `items` is keyed on the joined
  // codes, not on `sizes`, so editing a pair keeps the shift-click anchor.
  const codesKey = codes.join(',');
  const selectionItems = useMemo(() => (codesKey ? codesKey.split(',').map((id) => ({ id })) : []), [codesKey]);
  const {
    selectedIds,
    toggleSelect,
    selectAll,
    clearSelection,
    setSelected,
    isAllSelected,
    isSomeSelected,
    selectedCount,
  } = useVariantSelection({ items: selectionItems });
  // Press and hold on a checkbox cell, then drag, paints the run.
  const isSizeSelected = useCallback((code: string) => selectedIds.has(code), [selectedIds]);
  const { handleProps: dragHandleProps } = useDragSelect({ order: codes, isSelected: isSizeSelected, setSelected });
  const lastEpochRef = useRef(selectionEpoch);
  useEffect(() => {
    if (lastEpochRef.current === selectionEpoch) return;
    lastEpochRef.current = selectionEpoch;
    clearSelection();
  }, [selectionEpoch, clearSelection]);
  // The toolbar's one pick (always a complete pair once chosen).
  const [bulkPair, setBulkPair] = useState<PlatenAssignmentDraft>(EMPTY_PAIR);
  const applyBulk = (): void => {
    const targets = selectedCount > 0 ? codes.filter((code) => selectedIds.has(code)) : codes;
    onApplyPair(pt, targets, bulkPair);
  };

  // One chip per platen variant in use, from the complete rows, through the
  // ONE shared grouping and the ONE range formatter.
  const chips = useMemo(() => {
    const table = completeTable(sizes);
    const { groups } = groupSizesByPlatenVariant(
      Object.keys(table).map((sizeCode) => ({ sizeCode })),
      { rules: { [pt]: table }, productTypeCode: pt },
    );
    return groups.map((group) => ({
      key: group.key,
      label: t('platenSize.chip', {
        platen: group.assignment.platen,
        variant: platenVariantLabel(t, group.assignment.platenVariant),
        range: formatPlatenSizeRange(
          t,
          group.items.map((item) => item.sizeCode),
        ),
      }),
    }));
  }, [sizes, pt, t]);

  // Known sizes for this type: the pick-list is those only; otherwise the
  // store's full size vocabulary (loading, failed, or no products yet).
  const usesKnownSizes = usedSizeCodes !== null && usedSizeCodes.length > 0;
  const sizeOptionsForType: PrintSizeCodeOption[] = useMemo(() => {
    const base: PrintSizeCodeOption[] = usesKnownSizes
      ? usedSizeCodes.map((code) => ({ code, label: joinStoreNames(sizeStoreNamesByCode.get(code) ?? []) }))
      : sizeOptions;
    return base.map((opt) =>
      Object.prototype.hasOwnProperty.call(sizes, opt.code)
        ? { ...opt, disabled: true, disabledReason: t('production.printSizeRules.alreadyAdded') }
        : opt,
    );
  }, [usesKnownSizes, usedSizeCodes, sizeStoreNamesByCode, sizeOptions, sizes, t]);
  const typeSizeStatus: PrintSizeCodeComboboxStatus = usesKnownSizes ? 'loaded' : sizeStatus;

  // The used sizes not in the table yet: what "Add the sizes TEE uses" adds.
  const missingUsedSizes = useMemo(
    () => (usedSizeCodes ?? []).filter((code) => !Object.prototype.hasOwnProperty.call(sizes, code)),
    [usedSizeCodes, sizes],
  );

  // Admin: select, size, platen, platen variant, remove. Read-only: size,
  // platen, platen variant.
  const columnCount = admin ? 5 : 3;

  // Sizes the org's RIP routing sends to a different platen than this rule.
  const ripOverlaps = useMemo(() => {
    if (!ripMap) return [];
    const out: Array<{ code: string; ripPlaten: string }> = [];
    for (const code of codes) {
      const pair = sizes[code];
      if (!pair?.platen) continue;
      const ripPlaten = resolvePlaten(ripMap, pt, code);
      if (ripPlaten !== null && ripPlaten !== pair.platen) out.push({ code, ripPlaten });
    }
    return out;
  }, [ripMap, codes, sizes, pt]);

  return (
    <AccordionItem value={pt} className="rounded-lg border">
      <AccordionTrigger className="rounded-t-lg px-3 py-3 hover:bg-muted/30 hover:no-underline motion-reduce:transition-none">
        <div className="flex min-w-0 flex-1 flex-col gap-1 text-left">
          <div className="flex min-w-0 items-center gap-3">
            <Frame className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
            <span className="shrink-0 font-mono text-sm font-semibold">{pt}</span>
            {typeStoreNames && typeStoreNames.length > 0 && (
              <span className="min-w-0 truncate text-[11px] leading-4 text-muted-foreground">
                <span aria-hidden="true">· </span>
                {joinStoreNames(typeStoreNames)}
              </span>
            )}
            <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
              {t('platenSize.sizeCount', { count: codes.length })}
            </span>
          </div>
          {chips.length > 0 && (
            <div className="hidden flex-wrap gap-2 pl-7 sm:flex">
              {chips.map((chip) => (
                <span
                  key={chip.key}
                  className="rounded-md bg-background px-2 py-0.5 text-[11px] font-normal leading-4 ring-1 ring-border"
                >
                  {chip.label}
                </span>
              ))}
            </div>
          )}
        </div>
      </AccordionTrigger>
      <AccordionContent className="space-y-3 px-3 pb-4 pt-0">
        {/* Bulk set (admins): one pick, one click, for every size or the
            ticked ones. Wraps on narrow screens, never scrolls sideways. */}
        {admin && codes.length > 0 && (
          <div className="flex flex-wrap items-center gap-2" data-testid="platen-size-bulk-toolbar">
            <PlatenAssignmentSelects
              mode="combined"
              layout="inline"
              value={bulkPair}
              onChange={setBulkPair}
              sizeLabel={pt}
              productTypeLabel={pt}
              ariaLabel={t('platenSize.bulk.pickAria', { productType: pt })}
            />
            <Button
              type="button"
              onClick={applyBulk}
              disabled={!isComplete(bulkPair)}
              className="h-11 bg-threadheads-purple text-white hover:bg-threadheads-purple/90 motion-reduce:transition-none"
            >
              {selectedCount > 0
                ? t('platenSize.bulk.applySelected', { count: selectedCount })
                : t('platenSize.bulk.applyAll', { count: codes.length })}
            </Button>
            {selectedCount > 0 && (
              <Button
                type="button"
                variant="ghost"
                onClick={clearSelection}
                className="h-11 text-foreground motion-reduce:transition-none"
              >
                {t('platenSize.bulk.clear')}
              </Button>
            )}
            <span role="status" aria-live="polite" className="text-sm tabular-nums text-foreground">
              {selectedCount > 0 ? t('platenSize.bulk.selectedCount', { count: selectedCount, total: codes.length }) : ''}
            </span>
          </div>
        )}
        <div className="overflow-x-auto rounded-lg border">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-muted/50 text-left">
                {admin && (
                  <th className="w-12 px-3 py-2">
                    <span className="sr-only">{t('platenSize.bulk.columnSelect')}</span>
                    {codes.length > 0 && (
                      <Checkbox
                        checked={isAllSelected ? true : isSomeSelected ? 'indeterminate' : false}
                        onCheckedChange={selectAll}
                        aria-label={t('platenSize.bulk.selectAllAria', { productType: pt })}
                      />
                    )}
                  </th>
                )}
                <th className="w-28 px-3 py-2 text-xs font-semibold">{t('platenSize.columnSize')}</th>
                <th className="w-36 px-3 py-2 text-xs font-semibold">{t('platenSize.columnPlaten')}</th>
                <th className="px-3 py-2 text-xs font-semibold">{t('platenSize.columnVariant')}</th>
                {admin && (
                  <th className="w-14">
                    <span className="sr-only">{t('platenSize.columnRemove')}</span>
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {codes.length === 0 ? (
                <tr>
                  <td colSpan={columnCount} className="px-3 py-3 text-center text-xs text-muted-foreground">
                    {t('platenSize.noSizes')}
                  </td>
                </tr>
              ) : (
                codes.map((code) => {
                  const pair = sizes[code] ?? EMPTY_PAIR;
                  const saved = serverSizes?.[code];
                  const unset = !isComplete(pair);
                  const storeNames = sizeStoreNamesByCode.get(code);
                  const unsetId = `platen-size-unset-${pt}-${code}`;
                  const hasNote = admin && unset;
                  const selected = admin && selectedIds.has(code);
                  return (
                    <PlatenSizeRow
                      key={code}
                      hasNote={hasNote}
                      selected={selected}
                      leadingCells={admin ? 2 : 1}
                      columnCount={columnCount}
                      note={
                        <>
                          {admin && unset && (
                            <p id={unsetId} className="text-[11px] leading-4 text-amber-700">
                              {t('platenSize.unsetRow')}
                            </p>
                          )}
                        </>
                      }
                    >
                      {admin && (
                        // The whole cell is the hit area (44px, matching the
                        // Selects); the checkbox handles its own clicks.
                        <td
                          className="w-12 cursor-pointer select-none px-3 py-2 align-top"
                          {...dragHandleProps(code)}
                          onClick={(e) => {
                            if ((e.target as HTMLElement).closest('button')) return;
                            toggleSelect(code, e);
                          }}
                        >
                          <div className="flex h-11 items-center">
                            <Checkbox
                              checked={selected}
                              onCheckedChange={() => toggleSelect(code)}
                              onClick={(e) => {
                                if (e.shiftKey) {
                                  e.preventDefault();
                                  toggleSelect(code, e);
                                }
                              }}
                              aria-label={t('platenSize.bulk.selectRowAria', { size: code, productType: pt })}
                            />
                          </div>
                        </td>
                      )}
                      <td className="max-w-[10rem] px-3 py-2 align-top">
                        <span className="font-mono font-semibold">{code}</span>
                        {storeNames && storeNames.length > 0 && (
                          <div className="mt-0.5 truncate text-[11px] leading-4 text-muted-foreground">
                            {joinStoreNames(storeNames)}
                          </div>
                        )}
                      </td>
                      {admin ? (
                        <PlatenAssignmentSelects
                          layout="cells"
                          className="align-top"
                          value={pair}
                          onChange={(next) => onSetPair(pt, code, next)}
                          sizeLabel={code}
                          productTypeLabel={pt}
                          invalid={unset}
                          describedBy={unsetId}
                          changed={{
                            platen: pair.platen !== (saved?.platen ?? null),
                            platenVariant: pair.platenVariant !== (saved?.platenVariant ?? null),
                          }}
                        />
                      ) : (
                        <>
                          <td className="px-3 py-2 align-top font-mono">{pair.platen ?? '-'}</td>
                          <td className="px-3 py-2 align-top">
                            {pair.platenVariant ? platenVariantLabel(t, pair.platenVariant) : '-'}
                          </td>
                        </>
                      )}
                      {admin && (
                        <td className="px-2 py-2 align-top">
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-11 w-11 text-muted-foreground hover:text-foreground motion-reduce:transition-none"
                            aria-label={t('platenSize.removeSizeAria', { size: code, productType: pt })}
                            onClick={() => onRemoveSize(pt, code)}
                          >
                            <Trash2 className="h-4 w-4" aria-hidden="true" />
                          </Button>
                        </td>
                      )}
                    </PlatenSizeRow>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* RIP routing disagrees on some sizes: said ONCE for the type, not
            on every row. Never blocks Save. */}
        {ripOverlaps.length > 0 && (
          <p className="flex items-start gap-1.5 text-[11px] leading-4 text-amber-800" data-testid="platen-size-rip-overlap">
            <AlertTriangle className="mt-px h-3 w-3 shrink-0 text-amber-700" aria-hidden="true" />
            <span>
              {t('platenSize.ripOverlapSummary', {
                count: ripOverlaps.length,
                sizes: ripOverlaps
                  .map((o) => t('platenSize.ripOverlapItem', { size: o.code, platen: o.ripPlaten }))
                  .join(t('platenSize.ripOverlapSeparator')),
              })}
            </span>
          </p>
        )}

        {admin && (
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <PrintSizeCodeCombobox
              emptyLabel={t('production.codeOptions.noMatches')}
              options={sizeOptionsForType}
              status={typeSizeStatus}
              onSelect={(code) => onSelectSize(pt, code)}
              placeholder={t('platenSize.sizeComboboxPlaceholder')}
              ariaLabel={t('platenSize.sizeComboboxAria', { productType: pt })}
              addButtonLabel={t('platenSize.addSize')}
              createLabel={(code) => t('platenSize.createCode', { code })}
              errorHint={t('platenSize.codeTablesError')}
            />
            {usesKnownSizes && missingUsedSizes.length > 0 && (
              <Button
                type="button"
                variant="outline"
                className="h-11 gap-1 motion-reduce:transition-none"
                onClick={() => onAddSizes(pt, missingUsedSizes)}
              >
                <Plus className="h-4 w-4" aria-hidden="true" />
                {t('platenSize.addTypeSizes', { productType: pt, count: missingUsedSizes.length })}
              </Button>
            )}
            <Button
              type="button"
              variant="ghost"
              className="h-11 gap-1 text-muted-foreground hover:text-destructive motion-reduce:transition-none"
              onClick={() => onRemoveProductType(pt)}
            >
              <Trash2 className="h-4 w-4" aria-hidden="true" />
              {t('platenSize.removeType', { productType: pt })}
            </Button>
          </div>
        )}
        {admin && usedSizeCodes !== null && (
          <p className="text-xs text-muted-foreground" data-testid="platen-size-type-sizes-hint">
            {usesKnownSizes
              ? t('platenSize.typeSizesHint', { productType: pt })
              : t('platenSize.typeSizesNone', { productType: pt })}
          </p>
        )}
      </AccordionContent>
    </AccordionItem>
  );
}

/** A size row, plus a note row under it (unset / RIP overlap) when needed.
 *  The border sits under the pair so the note reads as part of its row. */
function PlatenSizeRow({
  children,
  hasNote,
  selected,
  note,
  leadingCells,
  columnCount,
}: {
  children: ReactNode;
  hasNote: boolean;
  /** A ticked row (bulk selection): tinted, note row included. */
  selected: boolean;
  note: ReactNode;
  /** Empty cells before the note (the select and size columns). */
  leadingCells: number;
  columnCount: number;
}): JSX.Element {
  const tint = selected ? 'bg-threadheads-purple/10' : undefined;
  return (
    <>
      <tr
        className={cn(hasNote ? 'align-top' : 'border-b align-top last:border-b-0', tint)}
        data-state={selected ? 'selected' : undefined}
      >
        {children}
      </tr>
      {hasNote && (
        <tr className={cn('border-b last:border-b-0', tint)}>
          {Array.from({ length: leadingCells }, (_, i) => (
            <td key={i} />
          ))}
          <td colSpan={columnCount - leadingCells} className="space-y-1 px-3 pb-2 pt-0">
            {note}
          </td>
        </tr>
      )}
    </>
  );
}
